// Dynamic image loading utilities
import { useState, useEffect } from 'react';

// Cache for loaded images to avoid re-imports
const imageCache = new Map();

/**
 * Hook for dynamically loading images using explicit imports
 * @param {string} imagePath - Path to the image
 * @returns {Object} - { src, loading, error }
 */
export const useDynamicImage = (imagePath) => {
  const [state, setState] = useState({
    src: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!imagePath) {
      setState({
        src: null,
        loading: false,
        error: new Error('No image path provided'),
      });
      return;
    }

    // Check cache first
    if (imageCache.has(imagePath)) {
      setState({ src: imageCache.get(imagePath), loading: false, error: null });
      return;
    }

    // Map paths to explicit dynamic imports
    let importPromise;

    switch (imagePath) {
      // Pokemon packs
      case '../games/pokemon/packs/151.webp':
        importPromise = import('../games/pokemon/packs/151.webp');
        break;
      case '../games/pokemon/packs/destined_rivals.webp':
        importPromise = import('../games/pokemon/packs/destined_rivals.webp');
        break;
      case '../games/pokemon/packs/black_bolt.webp':
        importPromise = import('../games/pokemon/packs/black_bolt.webp');
        break;
      case '../games/pokemon/packs/mega_evolution.webp':
      case '../games/pokemon/packs/mega_evolution.png': // tolerate legacy .png reference
        importPromise = import('../games/pokemon/packs/mega_evolution.webp');
        break;

      // MTG packs
      case '../games/mtg/packs/spiderman.webp':
        importPromise = import('../games/mtg/packs/spiderman.webp');
        break;
      case '../games/mtg/packs/eoe.webp':
        importPromise = import('../games/mtg/packs/eoe.webp');
        break;
      case '../games/mtg/packs/ff.webp':
        importPromise = import('../games/mtg/packs/ff.webp');
        break;
      case '../games/mtg/packs/tarkir_dragonstorm.webp':
        importPromise = import('../games/mtg/packs/tarkir_dragonstorm.webp');
        break;

      // One Piece packs
      case '../games/one-piece/packs/500.webp':
        importPromise = import('../games/one-piece/packs/500.webp');
        break;
      case '../games/one-piece/packs/two_legends.webp':
        importPromise = import('../games/one-piece/packs/two_legends.webp');
        break;
      case '../games/one-piece/packs/emperors.webp':
        importPromise = import('../games/one-piece/packs/emperors.webp');
        break;
      case '../games/one-piece/packs/wings.webp':
        importPromise = import('../games/one-piece/packs/wings.webp');
        break;

      default:
        const error = new Error(
          `No import mapping found for path: ${imagePath}`,
        );
        console.error(error.message);
        setState({ src: null, loading: false, error });
        return;
    }

    // Execute the dynamic import
    importPromise
      .then((module) => {
        const src = module.default;
        imageCache.set(imagePath, src);
        setState({ src, loading: false, error: null });
      })
      .catch((error) => {
        console.error(`Failed to load image: ${imagePath}`, error);
        setState({ src: null, loading: false, error });
      });
  }, [imagePath]);

  return state;
};

/**
 * Component for dynamically loaded images
 */
export const DynamicImage = ({
  imagePath,
  alt,
  className = '',
  fallback = null,
  loadingComponent = null,
  onLoad = () => {},
  onError = () => {},
  ...props
}) => {
  const { src, loading, error } = useDynamicImage(imagePath);

  if (loading) {
    return (
      loadingComponent || (
        <div className={`animate-pulse bg-gray-800 ${className}`} {...props}>
          <div className="flex h-full items-center justify-center">
            <span className="text-sm text-gray-400">Loading...</span>
          </div>
        </div>
      )
    );
  }

  if (error || !src) {
    if (fallback) return fallback;
    return (
      <div
        className={`flex items-center justify-center bg-gray-200 ${className}`}
        {...props}
      >
        <span className="text-sm text-gray-500">Failed to load</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onLoad={() => onLoad(src)}
      onError={(e) => onError(e, imagePath)}
      {...props}
    />
  );
};
